import { useEffect } from 'react'
import { CloudSlash, ArrowsClockwise } from '@phosphor-icons/react'

import { useAutoPageReload } from '@/hooks'
import { getTokenFromURL } from '@/utils'

import { useSnackbar } from '../../helpers/hooks/useSnackbar'
import { APP_COLORS } from '../../helpers/constants'

import NoToken from './NoToken'
import PackageCountSelection from './PackageCountSelection'
import ResidentSelection from './ResidentSelection'
import PackagePhoto from './PackagePhoto'
import AccessCode from './AccessCode'
import WeeklyAccessCode from './WeeklyAccessCode'
import DoorUnlocked from './DoorUnlocked'
import InactivityModal from './InactivityModal'
import Home from './Home'
import EnterCode from './EnterCode'
import ErrorScreen from './ErrorScreen'
import CarrierSelection from './CarrierSelection'
import DeliveryCodeEntry from './DeliveryCodeEntry'
import { usePackageRoom, SCREENS } from './hooks/usePackageRoom'
import { useInactivity } from './hooks/useInactivity'
import { useFullscreen } from './hooks/useFullscreen'
import { useOfflineMode } from './hooks/useOfflineMode'
import { useSynchroniseOfflinePackages } from './hooks/useSynchroniseOfflinePackages'
import AccessSuccessRoneResidences from './AccessSuccessRoneResidences'
import { STORAGE_KEYS } from './utils'

const INACTIVITY_EXCLUDED_SCREENS = [SCREENS.HOME]
const RONE_RESIDENCES_INACTIVITY_EXCLUDED_SCREENS = [
  SCREENS.HOME,
  SCREENS.ACCESS_CODE,
  SCREENS.DOOR_UNLOCKED,
]
const EXTENDED_TIMEOUT_SCREENS = [SCREENS.ENTER_CODE]

const PackageRoom = () => {
  const { displaySnackbar } = useSnackbar()
  const token = getTokenFromURL()

  const {
    currentScreen,
    packageCount,
    processedPackages,
    selectedResident,
    accessCode,
    weeklyCode,
    isLoading,
    handleDeliverPackage,
    handleDeliveryCodeSubmit,
    handlePackageCountSelected,
    handleCarrierSelected,
    handleResidentSelected,
    handlePhotoTaken,
    handleAccessCodeDone,
    handleReset,
    handleBack,
    handleEnterCode,
    handleVerificationSuccess,
    handleShowWeeklyCode,
    handleWeeklyCodeDone,
  } = usePackageRoom()

  const {
    isConnectionIssue,
    isOffline,
    hasBadConnection,
    setHasBadConnection,
    isRoneResidencesBuilding,
  } = useOfflineMode()
  const { isSyncing } = useSynchroniseOfflinePackages(isOffline, hasBadConnection, currentScreen)

  // Note: Auto-reload application at 3am daily
  useAutoPageReload({
    storageKey: STORAGE_KEYS.LAST_RELOAD_DATE,
    skipReloadCheck: () => isOffline || currentScreen !== SCREENS.HOME,
  })

  // Note: Reset bad connection status always when home screen is shown
  useEffect(() => {
    if (currentScreen === SCREENS.HOME) {
      setHasBadConnection(false)
    }
  }, [currentScreen])

  const { showInactivityModal, handleStayActive } = useInactivity(
    currentScreen,
    isRoneResidencesBuilding
      ? RONE_RESIDENCES_INACTIVITY_EXCLUDED_SCREENS
      : INACTIVITY_EXCLUDED_SCREENS,
    EXTENDED_TIMEOUT_SCREENS,
  )

  const { isFullscreen, toggleFullscreen } = useFullscreen(displaySnackbar)

  if (!token) {
    return <NoToken />
  }

  const renderScreen = () => {
    switch (currentScreen) {
      case SCREENS.HOME:
        return (
          <Home
            onDeliverPackage={handleDeliverPackage}
            onEnterCode={handleEnterCode}
            isFullscreen={isFullscreen}
            onToggleFullscreen={toggleFullscreen}
          />
        )

      case SCREENS.PACKAGE_COUNT:
        return <PackageCountSelection onSelect={handlePackageCountSelected} onBack={handleBack} />

      case SCREENS.CARRIER_SELECTION:
        return <CarrierSelection onSelect={handleCarrierSelected} onBack={handleBack} />

      case SCREENS.DELIVERY_CODE_ENTRY:
        return <DeliveryCodeEntry onSubmit={handleDeliveryCodeSubmit} onBack={handleBack} />

      case SCREENS.RESIDENT_SELECTION:
        return (
          <ResidentSelection
            onSelect={handleResidentSelected}
            onBack={handleBack}
            packageNumber={processedPackages + 1}
            totalPackages={packageCount}
          />
        )

      case SCREENS.PACKAGE_PHOTO:
        return (
          <PackagePhoto
            onPhotoTaken={handlePhotoTaken}
            onBack={handleBack}
            packageNumber={processedPackages + 1}
            totalPackages={packageCount}
            selectedResident={selectedResident}
            isLoading={isLoading}
          />
        )

      case SCREENS.ENTER_CODE:
        return (
          <EnterCode
            onBack={handleBack}
            onSuccess={handleVerificationSuccess}
            onShowWeeklyCode={handleShowWeeklyCode}
          />
        )

      case SCREENS.ACCESS_CODE:
        return isRoneResidencesBuilding ? (
          <AccessSuccessRoneResidences onDone={handleAccessCodeDone} />
        ) : (
          <AccessCode onDone={handleAccessCodeDone} accessCode={accessCode} />
        )

      case SCREENS.WEEKLY_ACCESS_CODE:
        return <WeeklyAccessCode onDone={handleWeeklyCodeDone} weeklyCode={weeklyCode} />

      case SCREENS.DOOR_UNLOCKED:
        return isRoneResidencesBuilding ? (
          <AccessSuccessRoneResidences onDone={handleReset} />
        ) : (
          <DoorUnlocked onDone={handleReset} />
        )

      case SCREENS.ERROR:
        return <ErrorScreen onDone={handleReset} />

      default:
        return null
    }
  }

  return (
    <div className="package-room">
      {renderScreen()}
      {showInactivityModal && (
        <InactivityModal onReset={handleReset} onStayActive={handleStayActive} />
      )}
      {isConnectionIssue && (
        <div className="package-room__offline-indicator package-room__offline-indicator--fixed">
          <CloudSlash size={16} weight="fill" color={APP_COLORS.red500} />
        </div>
      )}
      {isSyncing && (
        <div className="package-room__offline-indicator package-room__offline-indicator--fixed">
          <ArrowsClockwise size={16} weight="fill" color={APP_COLORS.blue500} />
        </div>
      )}
    </div>
  )
}

export default PackageRoom
